/**
 * Cached jsonata expression evaluator.
 *
 * Compiles expressions once and caches the result to avoid
 * repeated parsing of the same expression strings.
 */
import jsonata, { type Expression } from "jsonata";
import type { ExpressionContext } from "./context.types.ts";

const expressionCache = new Map<string, Expression>();

const getCompiledExpression = (expression: string): Expression => {
    const cached = expressionCache.get(expression);
    if (cached) return cached;

    const compiled = jsonata(expression);
    expressionCache.set(expression, compiled);

    return compiled;
};

/**
 * Safely evaluates a jsonata expression against a context object.
 * Returns `null` if the expression fails to compile or evaluate.
 */
export const evaluateExpression = async <T>(expression: string, context: ExpressionContext): Promise<T | null> => {
    try {
        const compiled = getCompiledExpression(expression);
        return await compiled.evaluate(context);
    } catch {
        return null;
    }
};
